"use client";

import { useSyncExternalStore, useCallback } from "react";
import { capture } from "@/lib/analytics/posthog";

const STORAGE_KEY = "compare-games";
const MAX_ITEMS = 5;

type CompareItem = {
  appid: number;
  name: string;
  capsule_image: string | null;
};

type CompareStore = CompareItem[];

const listeners = new Set<() => void>();

function getSnapshot(): string {
  if (typeof window === "undefined") return "[]";
  return localStorage.getItem(STORAGE_KEY) ?? "[]";
}

function getServerSnapshot(): string {
  return "[]";
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function notify() {
  for (const listener of listeners) listener();
}

function readStore(): CompareStore {
  try {
    return JSON.parse(getSnapshot()) as CompareStore;
  } catch {
    return [];
  }
}

function writeStore(items: CompareStore) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  notify();
}

export function useCompareStore() {
  const raw = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const items: CompareStore = (() => {
    try {
      return JSON.parse(raw) as CompareStore;
    } catch {
      return [];
    }
  })();

  const add = useCallback((item: CompareItem) => {
    const current = readStore();
    if (current.some((i) => i.appid === item.appid)) return;
    if (current.length >= MAX_ITEMS) return;
    const next = [...current, item];
    writeStore(next);
    // Only a real mutation is reported — the two early returns above are a
    // no-op for the user and would otherwise inflate "added" against the cap.
    capture("compare game added", { appid: item.appid, total: next.length });
  }, []);

  const remove = useCallback((appid: number) => {
    const current = readStore();
    const next = current.filter((i) => i.appid !== appid);
    writeStore(next);
    if (next.length !== current.length) {
      capture("compare game removed", { appid, total: next.length });
    }
  }, []);

  const toggle = useCallback((item: CompareItem) => {
    const current = readStore();
    if (current.some((i) => i.appid === item.appid)) {
      const next = current.filter((i) => i.appid !== item.appid);
      writeStore(next);
      capture("compare game removed", { appid: item.appid, total: next.length });
    } else {
      if (current.length >= MAX_ITEMS) return;
      const next = [...current, item];
      writeStore(next);
      capture("compare game added", { appid: item.appid, total: next.length });
    }
  }, []);

  const clear = useCallback(() => {
    writeStore([]);
  }, []);

  const has = useCallback(
    (appid: number) => items.some((i) => i.appid === appid),
    [items]
  );

  return { items, add, remove, toggle, clear, has };
}

export type { CompareItem };
